import { Box, Button, Grid, MenuItem, Typography } from "@mui/material";
import { statusOptionsMap } from "@pages/procurement/config/prStatusFilterOptions";
import React, { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { ReactComponent as FilterIcon } from "src/assets/icon/filter.svg";
import { ReactComponent as SearchIcon } from "src/assets/icon/search.svg";
import FilterMenu from "src/components/filter/FilterMenu";
import RHFDatePicker from "src/components/hook-form/RHFDatePicker";
import RHFSelect from "src/components/hook-form/RHFSelect";
import RHFTextField from "src/components/hook-form/RHFTextField";
import useLocales from "src/hooks/useLocales";
import { fDate } from "src/utils/formatTime";
import { PurchaseRequisitionStatus } from "../../types/purchaseRequisitionTypes";

export interface IProcurementFilterState {
  isSaveAsDraft?: boolean;
  documentNumber?: string;
  requesterName?: string;
  vendorId?: string;
  dueDeliveryDate?: Date | null;
  status?: PurchaseRequisitionStatus;
}

interface ProcurementFiltersProps {
  loader: boolean;
  filterState: IProcurementFilterState;
  setFilterState: React.Dispatch<React.SetStateAction<IProcurementFilterState>>;
  isDraft?: boolean;
}

const ProcurementFilters: React.FC<ProcurementFiltersProps> = ({
  loader,
  filterState,
  setFilterState,
  isDraft = false,
}) => {
  const { translate } = useLocales();
  const inputRef = React.useRef<HTMLTextAreaElement>(null);
  const [filters, setFilters] = useState<Record<string, any>>({});
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleOpenMenu = (e: React.MouseEvent<HTMLElement>) =>
    setAnchorEl(e.currentTarget);
  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const filterSettersMap = {
    documentNumber: (value: string) =>
      value
        ? {
            name: "documentNumber",
            value,
            displayValue: value,
            label: translate("document_number"),
          }
        : undefined,
    requesterName: (value: string) =>
      value
        ? {
            name: "requesterName",
            value,
            displayValue: value,
            label: translate("requester"),
          }
        : undefined,

    vendorId: (value: string) =>
      value
        ? {
            name: "vendorId",
            value,
            displayValue: value,
            label: translate("vendor_name"),
          }
        : undefined,

    dueDeliveryDate: (value: Date | null) =>
      value
        ? {
            name: "dueDeliveryDate",
            value,
            displayValue: fDate(value, "yyyy-MM-dd"),
            label: translate("due_delivery_date"),
          }
        : undefined,

    isSaveAsDraft: (value: boolean) =>
      value
        ? {
            name: "isSaveAsDraft",
            value,
            displayValue: "Draft",
            label: translate("draft"),
          }
        : undefined,

    status: (value: PurchaseRequisitionStatus) =>
      value !== undefined
        ? {
            name: "status",
            value,
            displayValue: translate(statusOptionsMap[value]),
            label: translate("review_screen.status"),
          }
        : undefined,
  };

  const handleFilters = (v: IProcurementFilterState) => {
    const newFilters: Record<string, any> = {};

    Object.entries(v).forEach(([k, val]) => {
      if (
        val !== undefined &&
        filterSettersMap[k as keyof typeof filterSettersMap]
      ) {
        const filterFn = filterSettersMap[
          k as keyof typeof filterSettersMap
        ] as any;
        const filterObj = filterFn(val);
        if (filterObj) newFilters[k] = filterObj;
      }
    });

    setFilters(newFilters);
  };

  const methods = useForm<IProcurementFilterState>({
    defaultValues: filterState,
  });
  const { handleSubmit, setValue, reset } = methods;

  const handleOnDelete = (key: string) => {
    setFilters((prev) => {
      const f = { ...prev };
      delete f[key];
      return f;
    });

    setValue(key as keyof IProcurementFilterState, undefined as any);
    setFilterState((prev) => ({
      ...prev,
      [key]: undefined as any,
    }));
  };

  useEffect(() => {
    reset(filterState);
    handleFilters(filterState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterState]);

  return (
    <FilterMenu
      filters={filters}
      inputRef={inputRef}
      anchorEl={anchorEl}
      open={open}
      handleOpenMenu={handleOpenMenu}
      handleCloseMenu={handleCloseMenu}
      handleOnDelete={handleOnDelete}
      loader={loader}
    >
      <FormProvider {...methods}>
        <form
          onSubmit={handleSubmit((values) => {
            setFilterState((prev) => ({
              ...prev,
              ...values,
            }));

            handleFilters(values);
            handleCloseMenu();
          })}
        >
          <Grid container spacing={2} sx={{ mb: 1 }}>
            <Grid item xs={12}>
              <Typography variant="h6">
                <FilterIcon height={18} width={18} /> {translate("Filters")}
              </Typography>
            </Grid>

            {!isDraft && (
              <Grid item xs={12} md={6}>
                <RHFTextField
                  name="documentNumber"
                  label={translate("document_number")}
                  size="small"
                  fullWidth
                />
              </Grid>
            )}

            <Grid item xs={12} md={6}>
              <RHFTextField
                name="requesterName"
                label={translate("requester")}
                size="small"
                fullWidth
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <RHFTextField
                name="vendorId"
                label={translate("vendor_name")}
                size="small"
                fullWidth
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <RHFDatePicker
                name="dueDeliveryDate"
                label={translate("due_delivery_date")}
              />
            </Grid>

            {!isDraft && (
              <Grid item xs={12} md={6}>
                <RHFSelect
                  name="status"
                  label={translate("review_screen.status")}
                  fullWidth
                  size="small"
                  onChange={(e: any) => {
                    const value =
                      e.target.value === ""
                        ? undefined
                        : (Number(e.target.value) as PurchaseRequisitionStatus);
                    setValue("status", value);
                  }}
                >
                  <MenuItem value="">
                    {translate("review_screen.status")}
                  </MenuItem>
                  {Object.entries(statusOptionsMap).map(([value, labelKey]) => (
                    <MenuItem key={value} value={value}>
                      {translate(labelKey)}
                    </MenuItem>
                  ))}
                </RHFSelect>
              </Grid>
            )}
          </Grid>

          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              variant="contained"
              size="small"
              startIcon={<SearchIcon stroke="white" height={20} width={20} />}
              type="submit"
            >
              {translate("finance_screen.search")}
            </Button>
          </Box>
        </form>
      </FormProvider>
    </FilterMenu>
  );
};

export default ProcurementFilters;
